import { NavLink } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { getDashboard } from '../lib/api';
import type { DashboardData } from '@pcp/shared';
import { Icon, type IconName } from '../lib/icons';

/** Fixed bottom tab bar shown on compact screens only (CSS-gated via
 *  .mobile-tabbar). Top destinations + a "More" button that opens the drawer.
 *  Apple HIG: keep to ≤5 items. */
const TABS: {
  to: string;
  icon: IconName;
  label: string;
  end?: boolean;
  /** Which dashboard "new" (unreviewed) count feeds this tab's badge, if any. */
  countKey?: keyof DashboardData['newItems'];
}[] = [
  { to: '/', icon: 'home', label: 'Home', end: true },
  { to: '/tickets', icon: 'tickets', label: 'Tickets', countKey: 'zoho' },
  { to: '/topics', icon: 'topics', label: 'Topics', countKey: 'discourse' },
  { to: '/backlog', icon: 'backlog', label: 'Backlog', countKey: 'azureDevops' },
];

export function MobileTabBar({ onMore }: { onMore: () => void }) {
  // Unreviewed ("new") counts per item type, for the tab badges.
  const { data: dashboard } = useQuery({
    queryKey: ['dashboard'],
    queryFn: getDashboard,
    staleTime: 30_000,
    refetchOnWindowFocus: true,
  });
  const newCounts = dashboard?.newItems;

  return (
    <nav className="mobile-tabbar" aria-label="Primary">
      {TABS.map((t) => {
        const count = t.countKey ? newCounts?.[t.countKey].count : undefined;
        return (
          <NavLink
            key={t.to}
            to={t.to}
            end={t.end}
            className={({ isActive }) => `mobile-tabbar-item ${isActive ? 'active' : ''}`}
          >
            <span className="mobile-tabbar-icon">
              <Icon name={t.icon} size={22} strokeWidth={2} />
              {!!count && (
                <span className="mobile-tabbar-badge" aria-label={`${count} new`}>
                  {count > 99 ? '99+' : count}
                </span>
              )}
            </span>
            {t.label}
          </NavLink>
        );
      })}
      <button type="button" className="mobile-tabbar-item" onClick={onMore}>
        <span className="mobile-tabbar-icon"><Icon name="more" size={22} strokeWidth={2} /></span>
        More
      </button>
    </nav>
  );
}
